import { useEffect, useState } from "react";
import { Loader2, Sparkles, RefreshCw, Calculator } from "lucide-react";
import { fileUrl } from "@/lib/api";
import { GradeResult } from "@/components/deck/GradeResult";
import { ReadingEditor } from "@/components/deck/ReadingEditor";

const GRADES = ["Amazing", "Great", "Good", "Below Good"];
const LANE = { special: "Special gear", legacy: "Legacy gear", other: "Standard or 3★ gear" };
const STATUS = { new: "New", reviewing: "Under review", published: "Published", declined: "Not used" };
const MODES = { pvp: "Arena & Atlantis", pve: "Historic & Tesla" };
const defaultReply = (r) => (r ? `Grade (${MODES[r.mode] || ""}): ${r.grade}. ${(r.notes || []).join(" ")}`.trim() : "");

export const GradeCard = ({ x, onGrade, onApprove, onRegrade }) => {
  const g = x.grade || {};
  const r = g.status === "ready" ? g.result : null;
  const saved = g.approved && x.review?.status !== "new";
  const [pick, setPick] = useState((saved && g.approved) || r?.grade || "Good");
  const [reply, setReply] = useState((saved && x.review?.reply) || defaultReply(r));
  const [reading, setReading] = useState(g.reading || null);
  useEffect(() => { setReading(g.reading || null); }, [g.gradedAt]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (r && !saved) { setPick(r.grade); setReply(defaultReply(r)); } }, [r?.grade, r?.score, r?.mode]); // eslint-disable-line react-hooks/exhaustive-deps
  const id = `grade-${x.id}`;
  const closed = ["published", "declined"].includes(x.review?.status);
  const mode = g.mode || x.mode || "pvp";
  return (
    <section className="lg-sub" data-testid={id}>
      <div style={{ display: "flex", gap: 8, alignItems: "baseline", flexWrap: "wrap", marginBottom: 8 }}>
        <h4 className="serif" style={{ margin: 0, fontSize: 15 }}>{x.heroName || x.hero}</h4>
        <span style={{ fontSize: 13, color: "var(--ink-soft)" }}>from <b>{x.name}</b> · {LANE[x.lane] || x.lane}</span>
        <select className="lg-select lg-mini" style={{ width: 170 }} value={mode} onChange={(e) => onRegrade({ mode: e.target.value })} data-testid={`${id}-mode`}>
          {Object.entries(MODES).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <span className="lg-pill">{STATUS[x.review?.status] || "New"}</span>
        {g.approved && <span className="lg-pill" data-testid={`${id}-approved`}>Approved: {g.approved}</span>}
        <span style={{ fontSize: 12, color: "var(--muted)", marginLeft: "auto" }}>{new Date(x.at).toLocaleString()}</span>
      </div>
      <div className="lg-photos" style={{ marginBottom: 10 }}>
        {["ability", "hat", "necklace", "ring"].map((k) => x.shotUrls?.[k] && (
          <a key={k} className="lg-thumb lg-thumb-lg" href={fileUrl(x.shotUrls[k])} target="_blank" rel="noreferrer" title={k}><img src={fileUrl(x.shotUrls[k])} alt={`${k} screenshot`} /></a>
        ))}
      </div>
      {x.notes && <p style={{ fontSize: 13, marginBottom: 8 }}>Member notes: “{x.notes}”</p>}
      {!g.status && !x.stale && <button className="lg-btn sm" onClick={onGrade} data-testid={`${id}-run`}><Sparkles size={13} />Read screenshots & grade</button>}
      {g.status === "grading" && !x.stale && <p className="lg-note" data-testid={`${id}-grading`}><Loader2 size={14} className="lg-spin" />Reading the numbers… about 20 seconds.</p>}
      {x.stale && <p className="lg-err" data-testid={`${id}-stale`}>Reading stopped (server restart). Run it again.</p>}
      {g.status === "failed" && <p className="lg-err" data-testid={`${id}-failed`}>Reading failed: {g.error}</p>}
      {r && <GradeResult r={r} reading={g.reading} testid={id} />}
      {reading && (
        <details style={{ margin: "8px 0" }}>
          <summary style={{ cursor: "pointer", fontSize: 13, fontWeight: 600 }} data-testid={`${id}-edit-toggle`}>Check or fix the numbers that were read</summary>
          <ReadingEditor reading={reading} onChange={setReading} testid={id} />
          <button className="lg-btn sm" style={{ marginTop: 8 }} onClick={() => onRegrade({ reading })} data-testid={`${id}-recalc`}><Calculator size={13} />Recalculate grade</button>
        </details>
      )}
      {(g.status === "ready" || g.status === "failed" || x.stale) && (
        <button className="lg-btn ghost sm" style={{ margin: "8px 0" }} onClick={onGrade} data-testid={`${id}-rerun`}><RefreshCw size={13} />Read screenshots again</button>
      )}
      {r && (
        <div style={{ display: "grid", gap: 8, marginTop: 6 }}>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <label style={{ fontSize: 13, fontWeight: 600 }}>Final grade</label>
            <select className="lg-select lg-mini" style={{ width: 160 }} value={pick} onChange={(e) => setPick(e.target.value)} data-testid={`${id}-pick`}>
              {GRADES.map((v) => <option key={v}>{v}</option>)}
            </select>
          </div>
          <textarea className="lg-textarea" value={reply} maxLength={1200} onChange={(e) => setReply(e.target.value)} data-testid={`${id}-reply`} />
          {closed && <p style={{ fontSize: 12, color: "var(--muted)" }}>Already {x.review.status === "published" ? "Published" : "Not used"}. Change it in the Gear Guide inbox.</p>}
          <div><button className="lg-btn sm" disabled={closed} onClick={() => onApprove(pick, reply)} data-testid={`${id}-approve`}>{g.approved ? "Update approval" : "Approve & send to member"}</button></div>
        </div>
      )}
    </section>
  );
};
